import React, { useState } from 'react';
import { MessageSquare, Phone, Mail, Calendar, Clock, AlertCircle, Send, User, Users, CheckCircle } from 'lucide-react';
import { ymd } from '../../shared/dateUtils';

interface TimeOffRequest {
  id: string;
  type: 'vacation' | 'sick' | 'personal' | 'block';
  startDate: string;
  endDate: string;
  reason: string;
  status: 'pending' | 'approved' | 'denied';
  submittedAt: string;
}

interface Message {
  id: string;
  from: string;
  to: string;
  subject: string;
  body: string;
  timestamp: string;
  read: boolean;
  type: 'client' | 'manager' | 'team' | 'system';
}

interface CommunicationCenterProps {
  barberId: string;
  barberName: string;
}

const CommunicationCenter: React.FC<CommunicationCenterProps> = ({ barberId, barberName }) => {
  const [activeTab, setActiveTab] = useState<'messages' | 'requests' | 'broadcast'>('messages');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'm1',
      from: 'John Smith',
      to: barberName,
      subject: 'Appointment Confirmation',
      body: 'Looking forward to my appointment tomorrow at 2 PM!',
      timestamp: '2 hours ago',
      read: true,
      type: 'client'
    },
    {
      id: 'm2',
      from: 'Manager',
      to: barberName,
      subject: 'Schedule Update',
      body: 'Your schedule for next week has been approved.',
      timestamp: '5 hours ago',
      read: false,
      type: 'manager'
    }
  ]);

  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([
    {
      id: 'r1',
      type: 'vacation',
      startDate: '2024-02-15',
      endDate: '2024-02-20',
      reason: 'Family vacation',
      status: 'pending',
      submittedAt: '2024-01-20'
    }
  ]);

  const [newMessage, setNewMessage] = useState({
    to: '',
    subject: '',
    body: '',
    type: 'client' as Message['type']
  });

  const [newRequest, setNewRequest] = useState({
    type: 'vacation' as TimeOffRequest['type'],
    startDate: '',
    endDate: '',
    reason: ''
  });

  const handleSendMessage = () => {
    const message: Message = {
      id: `m${messages.length + 1}`,
      from: barberName,
      to: newMessage.to,
      subject: newMessage.subject,
      body: newMessage.body,
      timestamp: 'Just now',
      read: false,
      type: newMessage.type
    };
    setMessages([message, ...messages]);
    setNewMessage({ to: '', subject: '', body: '', type: 'client' });
  };

  const handleSubmitRequest = () => {
    const request: TimeOffRequest = {
      id: `r${timeOffRequests.length + 1}`,
      ...newRequest,
      status: 'pending',
      submittedAt: ymd()
    };
    setTimeOffRequests([request, ...timeOffRequests]);
    setNewRequest({ type: 'vacation', startDate: '', endDate: '', reason: '' });
  };

  const typeColors = {
    client: 'bg-blue-100 text-blue-800',
    manager: 'bg-purple-100 text-purple-800',
    team: 'bg-green-100 text-green-800',
    system: 'bg-gray-100 text-gray-800'
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    denied: 'bg-red-100 text-red-800'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* DESIGN-LATER (2026-08-02): PREVIEW only — messages + time-off requests are held in local
          state and are NOT sent or saved yet. Wire to sms/email service + staffService.submitTimeOffRequest. */}
      <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
        ⚠️ Preview — messages and time-off requests aren't connected to real sending or storage yet.
      </div>
      <h3 className="text-xl font-bold mb-6">Communication Center</h3>

      {/* Tab Navigation */}
      <div className="flex border-b mb-6">
        <button
          onClick={() => setActiveTab('messages')}
          className={`px-4 py-2 -mb-px ${
            activeTab === 'messages'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Messages
            {messages.filter(m => !m.read).length > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {messages.filter(m => !m.read).length}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-2 -mb-px ${
            activeTab === 'requests'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Time Off Requests
          </div>
        </button>
        <button
          onClick={() => setActiveTab('broadcast')}
          className={`px-4 py-2 -mb-px ${
            activeTab === 'broadcast'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Broadcast
          </div>
        </button>
      </div>

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div className="space-y-4">
          {/* Compose New Message */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold mb-3">Compose Message</h4>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">To:</label>
                <input
                  type="text"
                  value={newMessage.to}
                  onChange={(e) => setNewMessage({ ...newMessage, to: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Client name or email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type:</label>
                <select
                  value={newMessage.type}
                  onChange={(e) => setNewMessage({ ...newMessage, type: e.target.value as Message['type'] })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="client">Client</option>
                  <option value="manager">Manager</option>
                  <option value="team">Team</option>
                </select>
              </div>
            </div>
            <input
              type="text"
              value={newMessage.subject}
              onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-3"
              placeholder="Subject"
            />
            <textarea
              value={newMessage.body}
              onChange={(e) => setNewMessage({ ...newMessage, body: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-3"
              rows={3}
              placeholder="Message"
            />
            <button
              onClick={handleSendMessage}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
            >
              <Send className="h-4 w-4" />
              Send Message
            </button>
          </div>

          {/* Message List */}
          <div className="space-y-2">
            {messages.map(message => (
              <div
                key={message.id}
                className={`border rounded-lg p-4 hover:bg-gray-50 ${
                  !message.read ? 'bg-blue-50 border-blue-300' : ''
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{message.from}</span>
                    <span className={`px-2 py-1 rounded text-xs ${typeColors[message.type]}`}>
                      {message.type}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">{message.timestamp}</span>
                </div>
                <h4 className="font-semibold mb-1">{message.subject}</h4>
                <p className="text-gray-600 text-sm">{message.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time Off Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-4">
          {/* Request Form */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold mb-3">Submit Time Off Request</h4>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Type:</label>
                <select
                  value={newRequest.type}
                  onChange={(e) => setNewRequest({ ...newRequest, type: e.target.value as TimeOffRequest['type'] })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal Day</option>
                  <option value="block">Block Time</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Start:</label>
                  <input
                    type="date"
                    value={newRequest.startDate}
                    onChange={(e) => setNewRequest({ ...newRequest, startDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End:</label>
                  <input
                    type="date"
                    value={newRequest.endDate}
                    onChange={(e) => setNewRequest({ ...newRequest, endDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
            </div>
            <textarea
              value={newRequest.reason}
              onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-3"
              rows={2}
              placeholder="Reason (optional)"
            />
            <button
              onClick={handleSubmitRequest}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
            >
              <Calendar className="h-4 w-4" />
              Submit Request
            </button>
          </div>

          {/* Request List */}
          <div className="space-y-2">
            {timeOffRequests.map(request => (
              <div key={request.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="font-medium capitalize">{request.type}</span>
                    <span className={`px-2 py-1 rounded text-xs ${statusColors[request.status]}`}>
                      {request.status}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">Submitted: {request.submittedAt}</span>
                </div>
                <div className="text-sm text-gray-600">
                  <p>From: {request.startDate} To: {request.endDate}</p>
                  {request.reason && <p>Reason: {request.reason}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Broadcast Tab */}
      {activeTab === 'broadcast' && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold mb-3">Broadcast to Clients</h4>
            <p className="text-sm text-gray-600 mb-4">
              Send a message to all your clients or a specific group
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Recipients:</label>
                <select className="w-full px-3 py-2 border rounded-lg">
                  <option>All Clients</option>
                  <option>VIP Clients</option>
                  <option>New Clients (&lt; 3 visits)</option>
                  <option>Regular Clients (&gt; 10 visits)</option>
                  <option>Clients with upcoming appointments</option>
                </select>
              </div>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Subject"
              />
              <textarea
                className="w-full px-3 py-2 border rounded-lg"
                rows={4}
                placeholder="Message to broadcast"
              />
              <div className="flex gap-2">
                <button className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600">
                  <Send className="h-4 w-4" />
                  Send Broadcast
                </button>
                <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Preview
                </button>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-4">
            <div className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
              <div className="flex items-center gap-3 mb-2">
                <Phone className="h-5 w-5 text-blue-500" />
                <h4 className="font-semibold">Schedule Change Alert</h4>
              </div>
              <p className="text-sm text-gray-600">
                Notify clients about schedule changes
              </p>
            </div>
            <div className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
              <div className="flex items-center gap-3 mb-2">
                <Mail className="h-5 w-5 text-green-500" />
                <h4 className="font-semibold">Promotion Announcement</h4>
              </div>
              <p className="text-sm text-gray-600">
                Share special offers with clients
              </p>
            </div>
            <div className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                <h4 className="font-semibold">Reminder Campaign</h4>
              </div>
              <p className="text-sm text-gray-600">
                Send appointment reminders
              </p>
            </div>
            <div className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="h-5 w-5 text-purple-500" />
                <h4 className="font-semibold">Thank You Message</h4>
              </div>
              <p className="text-sm text-gray-600">
                Send appreciation to loyal clients
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationCenter;